import { IAuthRepository } from '../../domain/repositories/IAuthRepository';
import { User } from '../../domain/entities/User';
import { Email } from '../../domain/value-objects/Email';
import { UserRole } from '../../domain/value-objects/UserRole';

/**
 * Mock Authentication Repository
 * Implements IAuthRepository for development/testing
 */
export class MockAuthRepository implements IAuthRepository {
  private currentUser: User | null = null;
  private users: User[] = [];

  constructor() {
    // Initialize with mock users
    this.users = [
      new User({
        id: 'user-1',
        workspaceId: 'workspace-1',
        googleId: null,
        email: new Email('director@coursue.edu'),
        passwordHash: 'hashed_password_director',
        fullName: 'Director Principal',
        role: 'director',
        avatar: undefined,
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      }),
      new User({
        id: 'user-2',
        workspaceId: 'workspace-1',
        googleId: 'google-teacher-123',
        email: new Email('teacher@coursue.edu'),
        passwordHash: null,
        fullName: 'Profesor García',
        role: 'teacher',
        avatar: undefined,
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      }),
      new User({
        id: 'user-3',
        workspaceId: 'workspace-1',
        googleId: null,
        email: new Email('student@coursue.edu'),
        passwordHash: 'hashed_password_student',
        fullName: 'Juan Estudiante',
        role: 'student',
        avatar: undefined,
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      }),
      new User({
        id: 'user-4',
        workspaceId: 'workspace-1',
        googleId: null,
        email: new Email('teacher2@coursue.edu'),
        passwordHash: 'hashed_password_teacher2',
        fullName: 'Profesora Martínez',
        role: 'teacher',
        avatar: undefined,
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      }),
    ];
  }

  async login(email: string, password: string): Promise<User> {
    // Mock login logic - find user by email
    const user = await this.findByEmail(email);

    if (!user) {
      // Create a new mock user for unknown emails
      let role: UserRole = 'student';
      let name = 'Usuario';

      if (email.includes('director')) {
        role = 'director';
        name = 'Director Principal';
      } else if (email.includes('teacher')) {
        role = 'teacher';
        name = 'Profesor García';
      } else if (email.includes('student')) {
        role = 'student';
        name = 'Juan Estudiante';
      } else {
        name = 'Usuario';
      }

      const newUser = new User({
        id: `user-${Math.random().toString(36).substring(7)}`,
        workspaceId: 'workspace-1',
        googleId: null,
        email: new Email(email),
        passwordHash: `hashed_${password}`,
        fullName: name,
        role,
        avatar: undefined,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      this.users.push(newUser);
      this.currentUser = newUser;
      return newUser;
    }

    this.currentUser = user;
    return user;
  }

  async loginWithGoogle(): Promise<void> {
    // In mock mode, simulate Google OAuth by creating/finding a Google user
    const mockUser = this.users.find((u) => u.isGoogleUser());
    if (mockUser) {
      this.currentUser = mockUser;
      return;
    }

    // Create a new Google user if not found
    const newUser = new User({
      id: `user-${Math.random().toString(36).substring(7)}`,
      workspaceId: 'workspace-1',
      googleId: `google-student-${Math.random().toString(36).substring(7)}`,
      email: new Email('google-user@coursue.edu'),
      passwordHash: null,
      fullName: 'Google User',
      role: 'student',
      avatar: undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    this.users.push(newUser);
    this.currentUser = newUser;
  }

  async register(email: string, password: string, fullName: string, role: UserRole, workspaceId: string): Promise<User> {
    // Check if user already exists
    const existingUser = await this.findByEmail(email);
    if (existingUser) {
      throw new Error('User already exists with this email');
    }

    // Create new user
    const newUser = new User({
      id: `user-${Math.random().toString(36).substring(7)}`,
      workspaceId,
      googleId: null,
      email: new Email(email),
      passwordHash: `hashed_${password}`,
      fullName,
      role,
      avatar: undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    this.users.push(newUser);
    this.currentUser = newUser;
    return newUser;
  }

  async findById(id: string): Promise<User | null> {
    return this.users.find((u) => u.id === id) ?? null;
  }

  async findByEmail(email: string): Promise<User | null> {
    return this.users.find((u) => u.email.value === email) ?? null;
  }

  async findByGoogleId(googleId: string): Promise<User | null> {
    return this.users.find((u) => u.googleId === googleId) ?? null;
  }

  async getCurrentUser(): Promise<User | null> {
    return this.currentUser;
  }

  async logout(): Promise<void> {
    this.currentUser = null;
  }

  async findByWorkspaceId(workspaceId: string): Promise<User[]> {
    return this.users.filter((u) => u.workspaceId === workspaceId);
  }

  async findByRole(workspaceId: string, role: UserRole): Promise<User[]> {
    return this.users.filter((u) => u.workspaceId === workspaceId && u.role === role);
  }

  async create(user: User): Promise<User> {
    this.users.push(user);
    return user;
  }

  async update(user: User): Promise<User> {
    const index = this.users.findIndex((u) => u.id === user.id);
    if (index === -1) {
      throw new Error(`User not found: ${user.id}`);
    }
    this.users[index] = user;
    return user;
  }

  async delete(id: string): Promise<void> {
    const index = this.users.findIndex((u) => u.id === id);
    if (index === -1) {
      throw new Error(`User not found: ${id}`);
    }
    this.users.splice(index, 1);

    // Clear current user if deleted
    if (this.currentUser?.id === id) {
      this.currentUser = null;
    }
  }
}
